import {
  beforeAfterSets,
  showcaseImages,
  snowplowImages,
  commercialShowcaseImages,
  skidSteerShowcaseImages,
  maintenanceShowcaseImages,
  hardscapingShowcaseImages,
  gardenShowcaseImages,
  webpToGallery600,
  type BeforeAfterSet,
  type ShowcaseImage,
} from "@/lib/image-map";

export interface GalleryPhoto {
  src: string;
  smallSrc?: string;
  fullSrc?: string;
  alt: string;
  category: string;
}

export interface GallerySlider {
  id: number;
  set: BeforeAfterSet;
  caption: string;
  category: string;
}

export interface GalleryCategory {
  label: string;
  value: string;
}

const GROUP_GARDEN = "Garden, Planting, Tree & Shrub Care";
const GROUP_MAINTENANCE = "Landscape Maintenance & Stonework";
const GROUP_RENOVATION = "Landscape Renovation & Skid Steer Services";

export const galleryCategories: GalleryCategory[] = [
  { label: "All", value: "All" },
  { label: GROUP_GARDEN, value: GROUP_GARDEN },
  { label: GROUP_MAINTENANCE, value: GROUP_MAINTENANCE },
  { label: GROUP_RENOVATION, value: GROUP_RENOVATION },
];

const categoryGroups: Record<string, string> = {
  "Garden & Planting": GROUP_GARDEN,
  "Tree & Shrub Care": GROUP_GARDEN,
  "Landscape Maintenance": GROUP_MAINTENANCE,
  "Stonework": GROUP_MAINTENANCE,
  "Landscape Renovation": GROUP_RENOVATION,
  "Skid Steer Services": GROUP_RENOVATION,
};

const commercialPhotoGroups: Record<string, string> = {
  "Commercial townhouse community landscaping - angle 3": GROUP_GARDEN,
  "Commercial retail storefront mulch and planting installation": GROUP_GARDEN,
  "Municipal park commercial landscape maintenance": GROUP_MAINTENANCE,
  "Commercial lawn mowing equipment in action": GROUP_MAINTENANCE,
};

const commercialSliderGroups: Record<number, string> = {
  9: GROUP_RENOVATION,
  28: GROUP_RENOVATION,
  29: GROUP_MAINTENANCE,
  42: GROUP_MAINTENANCE,
};

// Mapping contract: every photo needs a group via categoryGroups (by its base
// category) or commercialPhotoGroups (by alt) / commercialSliderGroups (by id).
// Snow Plowing is intentionally excluded from the gallery. Anything else that
// resolves to null is dropped, with a dev-mode warning below.
const groupValues = new Set<string>([GROUP_GARDEN, GROUP_MAINTENANCE, GROUP_RENOVATION]);

function resolveGroup(baseCategory: string, alt: string): string | null {
  if (groupValues.has(baseCategory)) return baseCategory;
  if (baseCategory === "Snow Plowing") return null;
  const override = commercialPhotoGroups[alt];
  if (override) return override;
  const group = categoryGroups[baseCategory] ?? null;
  if (!group && import.meta.env.DEV) {
    console.warn(`Gallery photo dropped (unmapped category "${baseCategory}"): ${alt}`);
  }
  return group;
}

const sliderCaptions: Record<number, string> = {
  1: "Tree Trimming & Pruning",
  2: "Hedge Trimming & Shaping",
  3: "Backyard Stone Border",
  4: "Bed Edging & Mulching",
  5: "Garden Bed Installation",
  6: "Shrub Pruning & Bed Care",
  7: "Overgrown Backyard Restoration",
  8: "Front Yard Bed Cleanup",
  9: "Commercial Lot Cleanup",
  10: "Driveway Edge Clearing",
  11: "Mulch Installation",
  12: "Roadside Clearing & Mulch",
  14: "Ornamental Conifer Shaping",
  15: "Roadside Hedge & Bed Cleanup",
  27: "Weeding, Pruning & Mulch Installation",
  28: "Commercial Municipal Lot Cleanup",
  29: "Commercial Pruning & Maintenance",
  32: "Roadside Overgrowth Clearing",
  33: "Brush & Shrub Line Clearing",
  34: "Wooded Side Yard Brush Removal",
  35: "Garden Bed Planting & Mulch",
  36: "Residential Mulch Bed Refresh",
  37: "Residential Overgrowth Clearing",
  38: "Backyard Gravel Installation",
  39: "Wooded Backyard Clearing",
  40: "Front Yard Shrub Pruning & Cleanup",
  41: "Roadside Hedge Trimming",
  42: "Commercial Roadside Clearing",
};

export const gallerySliders: GallerySlider[] = Object.entries(beforeAfterSets)
  .map(([id, set]) => {
    const numId = Number(id);
    return {
      id: numId,
      set,
      caption: sliderCaptions[numId] ?? set.alt,
      category: commercialSliderGroups[numId] ?? categoryGroups[set.category] ?? "",
    };
  })
  .filter((slider) => slider.category !== "")
  .sort((a, b) => a.id - b.id);

const themedPhotos: GalleryPhoto[] = [
  {
    src: maintenanceShowcaseImages.lakefront.desktop,
    smallSrc: maintenanceShowcaseImages.lakefront.mobile,
    alt: "Lakefront property landscape maintenance",
    category: "Landscape Maintenance",
  },
  {
    src: maintenanceShowcaseImages.scagMower.desktop,
    smallSrc: maintenanceShowcaseImages.scagMower.mobile,
    alt: "Commercial mower cutting a residential lawn",
    category: "Landscape Maintenance",
  },
  {
    src: maintenanceShowcaseImages.lawnStriping.desktop,
    smallSrc: maintenanceShowcaseImages.lawnStriping.mobile,
    alt: "Residential lawn mowing striping pattern",
    category: "Landscape Maintenance",
  },
  {
    src: gardenShowcaseImages.mulchDriveway.desktop,
    smallSrc: gardenShowcaseImages.mulchDriveway.mobile,
    alt: "Fresh mulch bed installation along residential driveway",
    category: "Garden & Planting",
  },
  {
    src: hardscapingShowcaseImages.stoneWalkway.desktop,
    smallSrc: hardscapingShowcaseImages.stoneWalkway.mobile,
    alt: "Stone walkway installation",
    category: "Stonework",
  },
  {
    src: commercialShowcaseImages.mulch.desktop,
    smallSrc: commercialShowcaseImages.mulch.mobile,
    alt: "Commercial retail storefront mulch and planting installation",
    category: "Commercial Services",
  },
  {
    src: commercialShowcaseImages.municipalPark.desktop,
    smallSrc: commercialShowcaseImages.municipalPark.mobile,
    alt: "Municipal park commercial landscape maintenance",
    category: "Commercial Services",
  },
  {
    src: commercialShowcaseImages.plowing.desktop,
    smallSrc: commercialShowcaseImages.plowing.mobile,
    alt: "Commercial snow plowing with plow truck",
    category: "Commercial Services",
  },
  {
    src: skidSteerShowcaseImages.commGrading.desktop,
    smallSrc: skidSteerShowcaseImages.commGrading.mobile,
    alt: "Skid steer commercial site grading",
    category: "Skid Steer Services",
  },
  {
    src: skidSteerShowcaseImages.residentialRemoval.desktop,
    smallSrc: skidSteerShowcaseImages.residentialRemoval.mobile,
    alt: "Skid steer residential material removal",
    category: "Skid Steer Services",
  },
  {
    src: snowplowImages.commercial.large,
    smallSrc: snowplowImages.commercial.small,
    alt: "Snow plow clearing a commercial parking lot",
    category: "Snow Plowing",
  },
  {
    src: snowplowImages.fleet.large,
    smallSrc: snowplowImages.fleet.small,
    alt: "TLC Associates snow plow fleet ready for winter",
    category: "Snow Plowing",
  },
];

const excludedAlts = new Set<string>([
  "Commercial townhouse community landscaping - angle 1",
  "Commercial townhouse community landscaping - angle 2",
  "Commercial townhouse community landscaping - angle 4",
  "Commercial snow plowing with plow truck",
  "Hedge Trimming & Shaping - completed work",
  "Mulch Installation - completed work",
  "Fresh mulch bed installation along residential driveway",
  "Residential landscape maintenance with mulch beds",
  "Skid steer heavy equipment clearing",
  "Wooded Side Yard Brush Removal - completed work",
]);

const featuredOrder: string[] = [
  "Precision lawn mowing concentric circles pattern",
  "Lakefront property maintenance",
  "Residential lawn mowing striping",
  "Lakefront property landscape maintenance",
  "Residential lawn mowing striping pattern",
  "Lawn care with flower bed maintenance",
  "Tree ring mulch bed planting",
  "Garden Bed Installation - completed work",
  "Garden Bed Planting & Mulch - completed work",
  "Weeding, Pruning & Mulch Installation - completed work",
  "Stone walkway installation",
  "Commercial mower cutting a residential lawn",
];

const trailingAlts = new Set<string>([
  "Commercial Pruning & Maintenance - completed work",
]);

function photoRank(photo: GalleryPhoto): number {
  if (trailingAlts.has(photo.alt)) return 20000;
  const featured = featuredOrder.indexOf(photo.alt);
  if (featured !== -1) return featured;
  return 10000;
}

function buildGalleryPhotos(): GalleryPhoto[] {
  const photos: GalleryPhoto[] = [];
  const bySrc = new Map<string, GalleryPhoto>();

  const add = (photo: GalleryPhoto) => {
    if (excludedAlts.has(photo.alt)) return;
    const group = resolveGroup(photo.category, photo.alt);
    if (!group) return;
    if (bySrc.has(photo.src)) return;
    const src600 = webpToGallery600[photo.src];
    if (!src600 && import.meta.env.DEV) {
      console.warn(`Gallery photo missing 600px copy: ${photo.alt}`);
    }
    const grouped: GalleryPhoto = {
      ...photo,
      category: group,
      src: src600 ?? photo.src,
      smallSrc: src600 ? undefined : photo.smallSrc,
      fullSrc: photo.src,
    };
    bySrc.set(photo.src, grouped);
    photos.push(grouped);
  };

  for (const entry of Object.values(showcaseImages)) {
    const items: ShowcaseImage[] = Array.isArray(entry) ? entry : [entry];
    for (const item of items) {
      add({ src: item.src, alt: item.alt, category: item.category });
    }
  }

  for (const photo of themedPhotos) {
    add(photo);
  }

  for (const { id, set, category } of gallerySliders) {
    add({
      src: set.after.large,
      smallSrc: set.after.small !== set.after.large ? set.after.small : undefined,
      alt: `${sliderCaptions[id] ?? set.alt} - completed work`,
      category,
    });
  }

  return photos.sort((a, b) => photoRank(a) - photoRank(b));
}

export const galleryPhotos: GalleryPhoto[] = buildGalleryPhotos();

const categoryFeatured: Record<string, string[]> = {
  [GROUP_GARDEN]: [
    "Tree ring mulch bed planting",
    "Garden Bed Planting & Mulch - completed work",
    "Commercial retail storefront mulch and planting installation",
    "Commercial townhouse community landscaping - angle 3",
    "Garden Bed Installation - completed work",
    "Weeding, Pruning & Mulch Installation - completed work",
  ],
  [GROUP_MAINTENANCE]: [
    "Precision lawn mowing concentric circles pattern",
    "Lakefront property maintenance",
    "Residential lawn mowing striping",
    "Municipal park commercial landscape maintenance",
    "Commercial lawn mowing equipment in action",
    "Lakefront property landscape maintenance",
  ],
  [GROUP_RENOVATION]: [
    "Skid steer land clearing - mulch pathway",
    "Overgrown Backyard Restoration - completed work",
    "Commercial Lot Cleanup - completed work",
    "Commercial Municipal Lot Cleanup - completed work",
    "Driveway Edge Clearing - completed work",
    "Bed Edging & Mulching - completed work",
  ],
};

export function photosForCategory(category: string): GalleryPhoto[] {
  if (category === "All") return galleryPhotos;
  const subset = galleryPhotos.filter((photo) => photo.category === category);
  const featured = categoryFeatured[category];
  if (!featured) return subset;
  const rank = (photo: GalleryPhoto) => {
    if (trailingAlts.has(photo.alt)) return 20000;
    const index = featured.indexOf(photo.alt);
    return index === -1 ? 10000 : index;
  };
  return [...subset].sort((a, b) => rank(a) - rank(b));
}
